/**
 * Queue Store
 * Manages scraping queue state and pause/resume controls
 */

import { api } from "@/utils/api";
import { socketService } from "@/utils/socket";
import { WSEvent, type WSEventPayload } from "@mobile-scraper/shared";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

export interface QueueStats {
  pendingJobsCount: number;
  processingJobsCount: number;
  completedJobsCount: number;
  failedJobsCount: number;
  totalJobs: number;
}

export const useQueueStore = defineStore("queue", () => {
  // State
  const stats = ref<QueueStats>({
    pendingJobsCount: 0,
    processingJobsCount: 0,
    completedJobsCount: 0,
    failedJobsCount: 0,
    totalJobs: 0,
  });
  const isScrapingPaused = ref(false);
  const isGlobalPaused = ref(false); // Admin-only global pause
  const loading = ref(false);
  const error = ref<string | null>(null);

  // Computed
  const queueProgress = computed(() => {
    if (stats.value.totalJobs === 0) return 0;
    const completed =
      stats.value.completedJobsCount + stats.value.failedJobsCount;
    return Math.round((completed / stats.value.totalJobs) * 100);
  });

  const hasActiveJobs = computed(() => {
    return (
      stats.value.pendingJobsCount > 0 || stats.value.processingJobsCount > 0
    );
  });

  const isAnyPaused = computed(() => {
    return isScrapingPaused.value || isGlobalPaused.value;
  });

  // Actions
  async function fetchQueueStats(): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      // Fetch both queue status, job stats, and scraping status for complete data
      const [queueStatus, jobStats, scrapingStatus] = await Promise.all([
        api.get<{ stats: { total: number; active: number; pending: number } }>(
          "/api/jobs/queue/status",
        ),
        api.get<{
          total: number;
          pending: number;
          processing: number;
          completed: number;
          failed: number;
        }>("/api/jobs/stats"),
        api.get<{ userPaused: boolean; globalPaused: boolean }>(
          "/api/scraping/status",
        ),
      ]);

      // Combine data from all endpoints
      stats.value = {
        pendingJobsCount: jobStats.pending,
        processingJobsCount: jobStats.processing,
        completedJobsCount: jobStats.completed,
        failedJobsCount: jobStats.failed,
        totalJobs: jobStats.total,
      };

      // Update pause states
      isScrapingPaused.value = scrapingStatus.userPaused;
      isGlobalPaused.value = scrapingStatus.globalPaused;
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : "Failed to fetch queue stats";
      console.error("[Queue Store] Fetch stats error:", err);
    } finally {
      loading.value = false;
    }
  }

  async function pauseScraping(): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      await api.post("/api/scraping/pause");
      isScrapingPaused.value = true;
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : "Failed to pause scraping";
      console.error("[Queue Store] Pause error:", err);
    } finally {
      loading.value = false;
    }
  }

  async function resumeScraping(): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      await api.post("/api/scraping/resume");
      isScrapingPaused.value = false;
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : "Failed to resume scraping";
      console.error("[Queue Store] Resume error:", err);
    } finally {
      loading.value = false;
    }
  }

  async function pauseScrapingGlobal(): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      await api.post("/api/scraping/pause-global");
      isGlobalPaused.value = true;
    } catch (err) {
      error.value =
        err instanceof Error
          ? err.message
          : "Failed to pause scraping globally";
      console.error("[Queue Store] Global pause error:", err);
    } finally {
      loading.value = false;
    }
  }

  async function resumeScrapingGlobal(): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      await api.post("/api/scraping/resume-global");
      isGlobalPaused.value = false;
    } catch (err) {
      error.value =
        err instanceof Error
          ? err.message
          : "Failed to resume scraping globally";
      console.error("[Queue Store] Global resume error:", err);
    } finally {
      loading.value = false;
    }
  }

  function updateStats(newStats: Partial<QueueStats>): void {
    stats.value = { ...stats.value, ...newStats };
  }

  function reset(): void {
    stats.value = {
      pendingJobsCount: 0,
      processingJobsCount: 0,
      completedJobsCount: 0,
      failedJobsCount: 0,
      totalJobs: 0,
    };
    isScrapingPaused.value = false;
    isGlobalPaused.value = false;
    error.value = null;
  }

  // WebSocket Listeners
  function setupWebSocketListeners(): void {
    socketService.on(
      WSEvent.SCRAPING_PAUSED,
      (payload: WSEventPayload[typeof WSEvent.SCRAPING_PAUSED]) => {
        isScrapingPaused.value = true;
        void payload;
      },
    );

    socketService.on(
      WSEvent.SCRAPING_RESUMED,
      (payload: WSEventPayload[typeof WSEvent.SCRAPING_RESUMED]) => {
        isScrapingPaused.value = false;
        void payload;
      },
    );

    socketService.on(
      WSEvent.SCRAPING_AUTO_PAUSED,
      (payload: WSEventPayload[typeof WSEvent.SCRAPING_AUTO_PAUSED]) => {
        isGlobalPaused.value = true;

        let message = `Scraping auto-paused: ${payload.reason}`;
        if (payload.triggerType) {
          message += ` (${payload.triggerType})`;
        }
        if (payload.details) {
          message += ` - ${payload.details}`;
        }

        void message;
      },
    );

    // Listen to global pause changes (admin pause/resume)
    socketService.on(
      WSEvent.GLOBAL_PAUSE_CHANGED,
      (payload: { globalPaused: boolean }) => {
        isGlobalPaused.value = payload.globalPaused;
      },
    );

    socketService.on(WSEvent.JOB_CREATED, () => {
      stats.value.pendingJobsCount += 1;
      stats.value.totalJobs += 1;
    });

    socketService.on(
      WSEvent.JOB_UPDATED,
      (payload: WSEventPayload[typeof WSEvent.JOB_UPDATED]) => {
        // Job started (pending -> processing)
        if (payload.status === "processing") {
          stats.value.pendingJobsCount -= 1;
          stats.value.processingJobsCount += 1;
        }
        // Job resumed (paused -> pending)
        else if (payload.status === "pending") {
          // No-op: counts don't change for paused/resumed
        }
        // Job paused (processing -> paused)
        else if (payload.status === "paused") {
          // No-op: job remains in counts
        }
      },
    );

    socketService.on(WSEvent.JOB_COMPLETED, () => {
      stats.value.processingJobsCount -= 1;
      stats.value.completedJobsCount += 1;
    });

    socketService.on(WSEvent.JOB_FAILED, () => {
      stats.value.processingJobsCount -= 1;
      stats.value.failedJobsCount += 1;
    });
  }

  // Initialize listeners on store creation
  setupWebSocketListeners();

  return {
    // State
    stats,
    isScrapingPaused,
    isGlobalPaused,
    loading,
    error,

    // Computed
    queueProgress,
    hasActiveJobs,
    isAnyPaused,

    // Actions
    fetchQueueStats,
    pauseScraping,
    resumeScraping,
    pauseScrapingGlobal,
    resumeScrapingGlobal,
    updateStats,
    reset,
    setupWebSocketListeners,
  };
});
